import React from 'react';
import { gql, useMutation, useQuery } from '@common/hooks';
import {
  ErrorHandler,
  ExternalLink,
  Notes,
  LoadingState,
  PostMessage,
} from '@common/components';
import { useQueryParam } from 'works/legacy/services/query-params-service';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CommentInfoFragmentType } from '@common/graphql';
import {
  CreateStaffingRequestAssignmentComment,
  CreateStaffingRequestAssignmentCommentArgs,
  CreateStaffingRequestAssignmentCommentResponse,
} from '@working-experience/graphql/mutations/create-staffing-request-assignment-comment';
import { buildDialogUrl } from '@common/services';

const ASSIGNMENT_NOTES_QUERY = gql`
  query StaffingRequestApplicationCommentsQuery($applicationId: ID!) {
    staffingRequestApplication(id: $applicationId) {
      id
      comments {
        id
      }
    }
  }
`;

type AssignmentNotesQueryResponse = {
  staffingRequestApplication: {
    id: string;
    comments: { id: string }[];
  };
};

type AssignmentNotesQueryArgs = {
  applicationId: string;
};

type ApplicationNotesExternalLinkProps = {
  applicationId: string;
  worksProfileId: string;
  currentEntityType: string;
};

const showSuccessMessage = () =>
  createMessage(
    'success',
    <>
      <span className="text-heavy">Note</span> successfully added
    </>
  );

const showErrorMessage = () =>
  createMessage('error', 'Something went wrong. Try again later.');

function ApplicationNotesExternalLink({
  applicationId,
  worksProfileId,
  currentEntityType,
}: ApplicationNotesExternalLinkProps) {
  const { data, loading, error } = useQuery<
    AssignmentNotesQueryResponse,
    AssignmentNotesQueryArgs
  >(ASSIGNMENT_NOTES_QUERY, {
    variables: { applicationId },
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ApplicationNotesExternalLink"
        messageType="contextual"
      />
    );
  }

  if (loading || !data) {
    return <LoadingState />;
  }

  const hasAnyApplicationNote =
    data.staffingRequestApplication.comments.length > 0;

  const submissionDrawerUrl = buildDialogUrl('review-submission-drawer', {
    props: { applicationId, currentTab: 'notes' },
    pathName: `${origin}/works/supplier/candidates/${worksProfileId}/submissions`,
  });

  const isCurrentMembershipOrganization = currentEntityType === 'Organization';
  const applicationNotesUrl = isCurrentMembershipOrganization
    ? `${origin}/works/applications/${applicationId}/notes`
    : submissionDrawerUrl;

  return (
    <div className="text-center">
      {hasAnyApplicationNote ? (
        <ExternalLink url={applicationNotesUrl}>View Notes</ExternalLink>
      ) : (
        <PostMessage.Empty>No notes from this phase</PostMessage.Empty>
      )}
    </div>
  );
}

type AssignmentNotesProps = {
  comments: CommentInfoFragmentType[];
  staffingRequestAssignmentId: string;
  staffingRequestApplicationId: string;
  candidateApplicationLink: string;
  currentEntityType: 'Organization' | 'Supplier';
  className?: string;
  preventNewNotes?: boolean;
  worksProfileId: string;
};

export function AssignmentNotes({
  comments,
  className,
  worksProfileId,
  preventNewNotes,
  currentEntityType,
  staffingRequestApplicationId,
  staffingRequestAssignmentId,
}: AssignmentNotesProps) {
  const shouldInitWithFormOpen = useQueryParam('action') === 'addNote';

  const [submitNote, { loading: mutationLoading }] = useMutation<
    CreateStaffingRequestAssignmentCommentResponse,
    CreateStaffingRequestAssignmentCommentArgs
  >(CreateStaffingRequestAssignmentComment, {
    onCompleted(payload) {
      if (payload) {
        showSuccessMessage();
      } else {
        showErrorMessage();
      }
    },
    onError() {
      showErrorMessage();
    },
  });

  function buildNoteName(comment: CommentInfoFragmentType) {
    return comment.external
      ? `${comment.author.name}, ${comment.author.entity?.name ?? ''}`
      : comment.author.name;
  }

  return (
    <Notes
      sections={[
        {
          title: 'Assignment',
          notes: comments.map((comment) => ({
            id: comment.id,
            name: buildNoteName(comment),
            time: comment.createdAt,
            message: comment.contentAsText,
            external: comment.external,
          })),
        },
        {
          title: 'Candidate Application',
          children: (
            <ApplicationNotesExternalLink
              worksProfileId={worksProfileId}
              currentEntityType={currentEntityType}
              applicationId={staffingRequestApplicationId}
            />
          ),
        },
      ]}
      preventNewNotes={preventNewNotes}
      initWithFormOpen={shouldInitWithFormOpen}
      infoTooltipText="Program Managers, Organization Admins, and Suppliers can add and view notes."
      notificationLabel="Notify users (Program Managers, Org Admins, and Suppliers)"
      className={className}
      noteSubmitLoading={mutationLoading}
      onNoteSubmit={({ note, notifyUsers }) => {
        submitNote({
          variables: {
            staffingRequestAssignmentId: staffingRequestAssignmentId,
            input: {
              contentAsText: note,
              notifyUsers,
            },
          },
        });
      }}
    />
  );
}
